import React, { useState } from 'react'
import LiveModal from 'live/components/ui/Modal'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import globalNotification from 'live/services/global-notification'
import Select from 'live/components/forms/ui/select'
import { RadioGroupCard } from '@intelie/ui'
import { recurrenceOptions, actionOptions } from '../utils'
import api from '../api'

const compareOptions = (first: EventTypeOption, second: EventTypeOption): number => {
    if (first.label < second.label) {
        return -1
    }
    if (first.label > second.label) {
        return 1
    }

    return 0
}

type Props = {
    eventTypeOptions: EventTypeOption[]
    closeModal: () => void
    insertPurgeRule: (purgeRule: PurgeRule) => void
    insertPurgeExceptionRule: (purgeExceptionRule: PurgeExceptionRule) => void
}

const PurgeModal = ({
    eventTypeOptions,
    closeModal,
    insertPurgeRule,
    insertPurgeExceptionRule
}: Props): JSX.Element => {
    const [eventType, setEventType] = useState('')
    const [eventTypeError, setEventTypeError] = useState(false)
    const [timeRange, setTimeRange] = useState('')
    const [timeRangeError, setTimeRangeError] = useState(false)
    const [recurrent, setRecurrent] = useState(false)
    const [action, setAction] = useState('DELETE')
    const [saving, setSaving] = useState(false)

    const savePurgeRule = (): void => {
        const purgeRule = { eventType, timeRange, recurrent }
        setSaving(true)
        api.savePurgeRule(purgeRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                insertPurgeRule(purgeRule)
                globalNotification.success(i18n('New purge rule sucessfully created'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    const timeRangeHasError = errorText.includes('time range')
                    if (errorText.includes('already exists')) {
                        globalNotification.error(i18n('Purge rule already exists'))
                    } else {
                        globalNotification.error(i18n('Failed to create the purge rule'))
                        setEventTypeError(eventTypeHasError)
                        setTimeRangeError(timeRangeHasError)
                    }
                })
            }
        })
    }

    const savePurgeExceptionRule = (): void => {
        const purgeExceptionRule = { eventType }
        setSaving(true)
        api.savePurgeExceptionRule(purgeExceptionRule).then(response => {
            setSaving(false)
            if (response.ok) {
                setEventTypeError(false)
                setTimeRangeError(false)
                insertPurgeExceptionRule(purgeExceptionRule)
                globalNotification.success(i18n('New purge exception sucessfully created'))
                closeModal()
            } else {
                response.json().then(errorResponse => {
                    const errorText = errorResponse.error
                    const eventTypeHasError = errorText.includes('event type')
                    if (errorText.includes('already exists')) {
                        globalNotification.error(i18n('Purge exception already exists'))
                    } else {
                        globalNotification.error(i18n('Failed to create the purge exception'))
                        setEventTypeError(eventTypeHasError)
                    }
                })
            }
        })
    }

    return (
        <LiveModal className="modal-sm" show={true} onHide={closeModal}>
            <LiveModal.Header>
                <LiveModal.Title>{i18n('New rule')}</LiveModal.Title>
            </LiveModal.Header>
            <LiveModal.Body>
                <div className="form-group">
                    <label>{i18n('Event type')}:</label>
                    <SelectContainer hasError={eventTypeError}>
                        <Select
                            creatable={true}
                            value={eventType}
                            placeholder={i18n('Insert an event type')}
                            options={eventTypeOptions.sort(compareOptions)}
                            onChange={eventType => setEventType(eventType)}
                            clearable={false}
                            searchable={true}
                        />
                    </SelectContainer>
                    {eventTypeError && (
                        <ErrorMessage>
                            <i className="far fa-exclamation-triangle" /> {i18n('Invalid event type')}
                        </ErrorMessage>
                    )}
                </div>
                <div className="form-group">
                    <label>{i18n('Time range')}:</label>
                    <Input
                        disabled={action === 'KEEP'}
                        hasError={timeRangeError}
                        autoFocus
                        type="text"
                        value={timeRange}
                        placeholder={i18n('Add a time range')}
                        onChange={e => setTimeRange(e.target.value)}
                    />
                    {timeRangeError && (
                        <ErrorMessage>
                            <i className="far fa-exclamation-triangle" /> {i18n('Invalid time range')}
                        </ErrorMessage>
                    )}
                </div>
                <div className="form-group">
                    <label>{i18n('Action')}:</label>
                    <RadioGroupCard
                        hideLabel={true}
                        label=""
                        name="action"
                        selectedOption={action}
                        options={actionOptions}
                        onChange={value => {
                            setAction(value)
                        }}
                    />
                </div>
                <div className="form-group">
                    <label>{i18n('Recurrence')}:</label>
                    <RadioGroupCard
                        hideLabel={true}
                        readonly={action === 'KEEP'}
                        label=""
                        name="recurrent"
                        selectedOption={recurrent ? 'RECURRENT' : 'ONE_TIME'}
                        options={recurrenceOptions}
                        onChange={value => {
                            if (value === 'RECURRENT') setRecurrent(true)
                            else setRecurrent(false)
                        }}
                    />
                </div>
            </LiveModal.Body>
            <LiveModal.Footer>
                <SubmitOrCancel
                    saving={saving}
                    onSubmit={action === 'DELETE' ? savePurgeRule : savePurgeExceptionRule}
                    onCancel={closeModal}
                    disabled={eventType === '' || (timeRange === '' && action === 'DELETE')}
                />
            </LiveModal.Footer>
        </LiveModal>
    )
}

const Input = styled.input<{ hasError?: boolean }>`
    margin-top: 16px;
    box-shadow: none !important;
    border-color: ${({ hasError, theme }) => (hasError ? `${theme['text-danger']} !important` : null)};
`

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme['text-danger']};
    font-size: 13px;
    margin-top: 5px;
`

const SelectContainer = styled.div<{ hasError?: boolean }>`
    div {
        border-color: ${({ hasError, theme }) => (hasError ? `${theme['text-danger']} !important` : null)};
        font-size: 13px;
        margin-bottom: 0px;
    }

    .Select-value-label {
        div {
            color: var(--gray-800);
        }
    }
`

export default PurgeModal
